/**
 * What the client is told when a send or the upload list gets no clear answer (2026-10-30).
 * Pure (no Firebase), so it can be tested on its own (tests/sendOutcome.test.ts).
 *
 * The Firebase SDK gives up on its own timer with the bare message "deadline-exceeded". It also uses the bare code as
 * the message on a lost connection ("internal") and on gateway errors. In all of these the browser simply does not
 * know what the server did, and sendRun may well have finished. The codes sendRun throws on purpose (no credits,
 * cool-down, platform fee, ...) are not in this list and keep their own message.
 */

/** sendRun's slowest run in the 30 days to 2026-10-30 was 23.4 s. Its own limit is 540 s. */
export const SEND_WAIT_MS = 180000;

/** Includes a cold start on a 256 MB instance (7–14 s measured 2026-10-30) plus the slowest run seen (8 s). */
export const UPLOAD_LIST_WAIT_MS = 120000;

const UNKNOWN_OUTCOME_CODES = new Set([
  'functions/deadline-exceeded',
  'functions/internal',
  'functions/unavailable',
  'functions/unknown',
]);

function errorCode(error: unknown): string {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : '';
}

export function isSendOutcomeUnknown(error: unknown): boolean {
  return UNKNOWN_OUTCOME_CODES.has(errorCode(error));
}

export function sendUnconfirmedMessage(): string {
  return 'We did not get an answer in time, but your send may have gone through. '
    + 'If it did, the number on this Run button drops and a cool-down timer appears. '
    + 'Please check that in a minute before trying again.';
}

export function uploadListErrorMessage(error: unknown): string {
  if (UNKNOWN_OUTCOME_CODES.has(errorCode(error))) {
    return 'The list of uploads took too long to load. Please try again in a minute.';
  }
  return error instanceof Error ? error.message : 'Failed to load uploads';
}
